import { FormControlLabel } from "@mui/material";
import { FileButton } from "components/shared/MultiStep/ImportFile";
import SimpleHelpdeskIcon from "components/shared/SimpleHelpdeskIcon";
import StyledButton from "components/shared/StyledButton";
import StyledCheckbox from "components/shared/StyledCheckbox";
import TextButton from "components/shared/TextButton";
import type { DeclarationsCardsData, URSSAFBeneficiaryError } from "containers/DeclarationPage/utils";
import { formatLocaleDateString } from "helpers/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";
import { generateDeclaration } from "store/contributionStore";
import type { IPayment } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../../assets/styles/_variables.module.scss";
import { selectSiblingCompanies } from "../../../../store/companyGroups/companyGroupsStore";
import { getReducedErrors, getReducedPayments } from "../utils";
import styles from "./style.module.scss";

interface IDownloadDeclarationStep {
    handlePrevious: () => void;
    /**
     * Data of current declaration as set by getDeclarationCardsData (utils).
     */
    declarationData: IDeclaration | DeclarationsCardsData | undefined;
    handleClose: () => void;
    /**
     * All payments regardless of status.
     */
    allPayments: IPayment[];
    /**
     * URSSAF errors.
     */
    errors: URSSAFBeneficiaryError[];
}

const DownloadDeclarationStep = ({
    handlePrevious,
    declarationData,
    handleClose,
    allPayments,
    errors,
}: IDownloadDeclarationStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const siblingCompanies = useAppSelector(selectSiblingCompanies);
    const canMerge = siblingCompanies.length > 0 && declarationData?.type === "urssaf";
    const [selectedMergeIds, setSelectedMergeIds] = useState<number[]>([]);

    const toggleMergeId = (id: number) => {
        setSelectedMergeIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    };
    const getPartnerLabel = (id: number) => {
        const company = siblingCompanies.find((c) => c.id === id);
        return company ? company.name : t("Declarations.DownloadStep.MergeUnknownCompany", { id });
    };

    const downloadFileButtonTitle =
        declarationData && declarationData?.quarter > 0
            ? t("Declarations.DownloadStep.DeclarationDateQuarter", {
                  quarter: declarationData?.quarter,
                  year: declarationData?.year,
              })
            : t("Declarations.DownloadStep.DeclarationDateYear", { year: declarationData?.year });

    const handleURSSAFExport = async (format?: "excel") => {
        if (declarationData) {
            const response = await dispatch(
                generateDeclaration({
                    declaration: declarationData,
                    format: format,
                    mergeWithCompanyIds: canMerge && selectedMergeIds.length > 0 ? selectedMergeIds : undefined,
                }),
            );
            if (response.meta.requestStatus === "fulfilled" && !format) {
                handleClose();
            }
        }
    };

    const reducedPayments = useMemo(() => {
        if (!allPayments) return [];
        return getReducedPayments(allPayments);
    }, [allPayments]);

    return (
        <div className={styles["urssaf-download-step"]}>
            <div className={styles["table-header"]}>
                <h1>{t("Declarations.DeclareModal.TitleStep2")}</h1>
                <span>
                    {t("Declarations.DeclareModal.SubtitleStep2", {
                        date: formatLocaleDateString({ date: declarationData?.endDate as Date }),
                    })}
                </span>
                {allPayments.length > 0 ? (
                    <span className={styles["number-info"]}>
                        {t("Declarations.DownloadStep.NumberBeneficiariesInfo", {
                            beneficiaries: reducedPayments.length - getReducedErrors(errors).length,
                            payments: reducedPayments.length,
                        })}
                    </span>
                ) : (
                    <span className={styles["number-info"]}>{t("Declarations.DownloadStep.NoDataInfo")}</span>
                )}
            </div>
            {canMerge && (
                <div className={styles["merge-section"]}>
                    <span className={styles["merge-title"]}>{t("Declarations.DownloadStep.MergeTitle")}</span>
                    <span className={styles["merge-description"]}>
                        {t("Declarations.DownloadStep.MergeDescription")}
                    </span>
                    <div className={styles["merge-options"]}>
                        {siblingCompanies.map((company) => (
                            <FormControlLabel
                                className="m-0!"
                                key={company.id}
                                control={
                                    <StyledCheckbox
                                        checked={selectedMergeIds.includes(company.id)}
                                        onChange={() => toggleMergeId(company.id)}
                                    />
                                }
                                label={getPartnerLabel(company.id)}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className={styles["files-steps-container"]}>
                <div className={styles["step"]}>
                    <span className={styles["step-title"]}>{t("Declarations.DeclareModal.Step", { number: 1 })}</span>
                    <span className={styles["step-text"]}>{t("Declarations.DownloadStep.Step2Text")}</span>
                    <FileButton
                        label={downloadFileButtonTitle}
                        style={{ marginTop: "0.5rem", width: "100%" }}
                        onClick={() => handleURSSAFExport()}
                        buttonColor="black"
                    />
                </div>
                <div className={styles["step"]}>
                    <span className={styles["step-title"]}>{t("Declarations.DeclareModal.Step", { number: 2 })}</span>
                    <p className={styles["step-text"]}>
                        {t("Declarations.DownloadStep.Step3Text")}{" "}
                        <SimpleHelpdeskIcon
                            link={
                                "https://helpdesk.crealo.app/fr/article/comment-deposer-le-fichier-de-declaration-urssaf-sur-mon-compte-diffuseur-5zld1d/"
                            }
                            text={t("Declarations.DownloadStep.HowTo")}
                        />
                    </p>
                </div>
                <div className={styles["step"]}>
                    <span className={styles["step-title"]}>{t("Declarations.DownloadStep.LastStep")}</span>
                    <TextButton
                        text={t("Declarations.DownloadStep.DownloadExcel")}
                        style={{ fontSize: "13px", color: "#141516", textDecoration: "underline" }}
                        colorOnHover={variables["foreground"]}
                        handleClick={() => handleURSSAFExport("excel")}
                    />
                </div>
            </div>
            <div className={styles["action-buttons"]}>
                <StyledButton buttonType="secondary" onClick={handlePrevious}>
                    {t("Declarations.DownloadStep.Return")}
                </StyledButton>
            </div>
        </div>
    );
};

export default DownloadDeclarationStep;
